import {
  GL_DETAIL_NUMERIC_COLUMNS,
  QBO_GL_DETAIL_SEARCH_COLUMN,
  QBO_GL_DETAIL_TABLE,
  duckDbIdent,
  glDetailColumnLabel,
  normalizeGlDetailColumnId,
  sqlString,
} from '../quickBooksGlDetailShared'

export type CfoGlDetailSortDirection = 'asc' | 'desc'
export type CfoGlDetailFilterMode = 'direct' | 'expression'
export type CfoGlDetailDistinctValueSort = 'count' | 'value'
export type CfoGlDetailFilterOperator =
  | 'contains'
  | 'equals'
  | 'not_equals'
  | 'starts_with'
  | 'ends_with'
  | 'blank'
  | 'not_blank'
  | 'gt'
  | 'gte'
  | 'lt'
  | 'lte'

export type CfoGlDetailFilterState = {
  column: string
  mode?: CfoGlDetailFilterMode
  values: string[]
  /** Direct mode only: keep rows whose value is NOT in `values` (NULL/blank rows are kept). */
  negate?: boolean
  operator?: CfoGlDetailFilterOperator
  expression?: string
}

export type CfoGlDetailQueryState = {
  search: string
  startDate: string
  endDate: string
  filter?: CfoGlDetailFilterState
  filters?: CfoGlDetailFilterState[]
  scopes?: Array<Pick<CfoGlDetailQueryState, 'search' | 'startDate' | 'endDate' | 'filter' | 'filters'>>
  /**
   * Pre-built SQL predicates ANDed into the WHERE clause. Internal-only: the
   * GL profile pane compiles its card selections (date/amount bins, blank
   * values) into predicates the filter vocabulary above can't express.
   */
  rawPredicates?: string[]
  sortColumn: string
  sortDirection: CfoGlDetailSortDirection
  visibleColumns: string[]
  page: number
  pageSize: number
  offset?: number
}

export type CfoGlDetailTotals = {
  rowCount: number
  minDate: string
  maxDate: string
  totals: Record<string, number>
}

export const EXCEL_WORKSHEET_ROW_LIMIT = 1_048_576

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/

export function normalizeGlDate(value: string | null | undefined): string {
  const text = String(value || '').trim().slice(0, 10)
  return DATE_RE.test(text) ? text : ''
}

export function allowedGlColumns(columns: string[]): string[] {
  const seen = new Set<string>()
  const out: string[] = []
  for (const column of columns) {
    const normalized = normalizeGlDetailColumnId(column)
    if (!normalized || seen.has(normalized)) continue
    seen.add(normalized)
    out.push(normalized)
  }
  return out
}

export function requireAllowedGlColumn(column: string, allowedColumns: string[], fallback?: string): string {
  const normalized = normalizeGlDetailColumnId(column)
  if (allowedColumns.includes(normalized)) return normalized
  if (fallback && allowedColumns.includes(fallback)) return fallback
  throw new Error(`GL detail column is not available: ${column || '(blank)'}`)
}

export function effectiveGlFilters(state: Pick<CfoGlDetailQueryState, 'filter' | 'filters'>): CfoGlDetailFilterState[] {
  const filters = Array.isArray(state.filters) ? state.filters : []
  const legacy = state.filter ? [state.filter] : []
  return (filters.length ? filters : legacy)
    .map((filter) => ({
      column: normalizeGlDetailColumnId(filter.column),
      mode: filter.mode || 'direct',
      values: Array.from(new Set((filter.values || []).map((value) => String(value ?? '').trim()).filter(Boolean))),
      negate: Boolean(filter.negate),
      operator: filter.operator || 'contains',
      expression: String(filter.expression ?? '').trim(),
    }))
    .filter((filter) => filter.column)
}

export function selectVisibleGlColumns(visibleColumns: string[], availableColumns: string[]): string[] {
  const allowed = allowedGlColumns(availableColumns)
  const selected = allowedGlColumns(visibleColumns).filter((column) => allowed.includes(column))
  if (selected.length) return selected
  return allowed.slice(0, Math.min(12, allowed.length))
}

function buildCfoGlDetailPredicates(
  state: Pick<CfoGlDetailQueryState, 'search' | 'startDate' | 'endDate' | 'filter' | 'filters' | 'rawPredicates'>,
  allowed: string[],
): string[] {
  const predicates: string[] = []
  const startDate = normalizeGlDate(state.startDate)
  const endDate = normalizeGlDate(state.endDate)
  if (allowed.includes('txn_date') && startDate) {
    predicates.push(`TRY_CAST(${duckDbIdent('txn_date')} AS DATE) >= DATE ${sqlString(startDate)}`)
  }
  if (allowed.includes('txn_date') && endDate) {
    predicates.push(`TRY_CAST(${duckDbIdent('txn_date')} AS DATE) <= DATE ${sqlString(endDate)}`)
  }
  const search = String(state.search || '').trim().toLowerCase()
  if (search) {
    const pattern = `%${search.replace(/[%_\\]/g, '\\$&')}%`
    if (allowed.includes(QBO_GL_DETAIL_SEARCH_COLUMN)) {
      predicates.push(`${duckDbIdent(QBO_GL_DETAIL_SEARCH_COLUMN)} LIKE ${sqlString(pattern)} ESCAPE '\\'`)
    } else {
      const searchable = allowed
        .filter((column) => !GL_DETAIL_NUMERIC_COLUMNS.has(column) && !column.startsWith('__'))
        .slice(0, 40)
      if (searchable.length) {
        predicates.push(`(${searchable.map((column) => `lower(CAST(${duckDbIdent(column)} AS VARCHAR)) LIKE ${sqlString(pattern)} ESCAPE '\\'`).join(' OR ')})`)
      }
    }
  }
  for (const filter of effectiveGlFilters(state)) {
    const filterColumn = normalizeGlDetailColumnId(filter.column)
    if (!filterColumn || !allowed.includes(filterColumn)) continue
    const expr = duckDbIdent(filterColumn)
    if ((filter.mode || 'direct') === 'expression') {
      const operator = filter.operator || 'contains'
      const value = String(filter.expression || '').trim()
      const textExpr = `CAST(${expr} AS VARCHAR)`
      const lowerExpr = `lower(${textExpr})`
      const escaped = value.toLowerCase().replace(/[%_\\]/g, '\\$&')
      if (operator === 'blank') {
        predicates.push(`(${expr} IS NULL OR trim(${textExpr}) = '')`)
      } else if (operator === 'not_blank') {
        predicates.push(`(${expr} IS NOT NULL AND trim(${textExpr}) <> '')`)
      } else if (value) {
        if (operator === 'equals') predicates.push(`${textExpr} = ${sqlString(value)}`)
        if (operator === 'not_equals') predicates.push(`${textExpr} <> ${sqlString(value)}`)
        if (operator === 'contains') predicates.push(`${lowerExpr} LIKE ${sqlString(`%${escaped}%`)} ESCAPE '\\'`)
        if (operator === 'starts_with') predicates.push(`${lowerExpr} LIKE ${sqlString(`${escaped}%`)} ESCAPE '\\'`)
        if (operator === 'ends_with') predicates.push(`${lowerExpr} LIKE ${sqlString(`%${escaped}`)} ESCAPE '\\'`)
        if (['gt', 'gte', 'lt', 'lte'].includes(operator)) {
          const castExpr = filterColumn === 'txn_date' || filterColumn.endsWith('_date')
            ? `TRY_CAST(${expr} AS DATE)`
            : GL_DETAIL_NUMERIC_COLUMNS.has(filterColumn)
              ? `TRY_CAST(${expr} AS DOUBLE)`
              : textExpr
          const castValue = filterColumn === 'txn_date' || filterColumn.endsWith('_date')
            ? `DATE ${sqlString(normalizeGlDate(value) || value)}`
            : GL_DETAIL_NUMERIC_COLUMNS.has(filterColumn)
              ? `TRY_CAST(${sqlString(value)} AS DOUBLE)`
              : sqlString(value)
          const op = operator === 'gt' ? '>' : operator === 'gte' ? '>=' : operator === 'lt' ? '<' : '<='
          predicates.push(`${castExpr} ${op} ${castValue}`)
        }
      }
      continue
    }
    const filterValues = Array.from(new Set((filter.values || []).map((value) => String(value ?? '').trim()).filter(Boolean)))
    if (filterValues.length) {
      const inList = `CAST(${duckDbIdent(filterColumn)} AS VARCHAR) IN (${filterValues.map(sqlString).join(', ')})`
      predicates.push(filter.negate
        ? `(${duckDbIdent(filterColumn)} IS NULL OR NOT ${inList})`
        : inList)
    }
  }
  for (const raw of state.rawPredicates || []) {
    const predicate = String(raw || '').trim()
    if (predicate) predicates.push(`(${predicate})`)
  }
  return predicates
}

export function buildCfoGlDetailWhereSql(state: CfoGlDetailQueryState, availableColumns: string[]): string {
  const allowed = allowedGlColumns(availableColumns)
  const predicates = buildCfoGlDetailPredicates(state, allowed)
  const scopePredicates = (state.scopes || [])
    .map((scope) => buildCfoGlDetailPredicates(scope, allowed))
    .filter((scope) => scope.length)
  if (scopePredicates.length) {
    predicates.push(`(${scopePredicates.map((scope) => `(${scope.join(' AND ')})`).join(' OR ')})`)
  }
  return predicates.length ? `WHERE ${predicates.join(' AND ')}` : ''
}

export function buildCfoGlDetailRowsSql(args: {
  state: CfoGlDetailQueryState
  availableColumns: string[]
  tableName?: string
  maxPageSize?: number
}): string {
  const { state, availableColumns, tableName = QBO_GL_DETAIL_TABLE } = args
  const visible = selectVisibleGlColumns(state.visibleColumns, availableColumns)
  const sortColumn = requireAllowedGlColumn(state.sortColumn, availableColumns, visible[0])
  const sortDirection = state.sortDirection === 'asc' ? 'ASC' : 'DESC'
  const maxPageSize = Math.max(1, Math.floor(Number(args.maxPageSize || 5000)))
  const pageSize = Math.max(1, Math.min(maxPageSize, Math.floor(Number(state.pageSize) || 250)))
  const page = Math.max(0, Math.floor(Number(state.page) || 0))
  const offset = Math.max(0, Math.floor(Number(state.offset ?? page * pageSize) || 0))
  const whereSql = buildCfoGlDetailWhereSql(state, availableColumns)
  const tieBreak = sortColumn === 'txn_date'
    ? ''
    : availableColumns.includes('txn_date')
      ? `, TRY_CAST(${duckDbIdent('txn_date')} AS DATE) DESC`
      : ''
  return [
    `SELECT ${visible.map(duckDbIdent).join(', ')}`,
    `FROM ${duckDbIdent(tableName)}`,
    whereSql,
    `ORDER BY ${GL_DETAIL_NUMERIC_COLUMNS.has(sortColumn) ? `TRY_CAST(${duckDbIdent(sortColumn)} AS DOUBLE)` : duckDbIdent(sortColumn)} ${sortDirection} NULLS LAST${tieBreak}`,
    `LIMIT ${pageSize}`,
    `OFFSET ${offset}`,
  ].filter(Boolean).join('\n')
}

export function buildCfoGlDetailTotalsSql(args: {
  state: CfoGlDetailQueryState
  availableColumns: string[]
  numericColumns?: string[]
  tableName?: string
}): string {
  const { state, availableColumns, tableName = QBO_GL_DETAIL_TABLE } = args
  const allowed = allowedGlColumns(availableColumns)
  const numericColumns = allowedGlColumns(args.numericColumns || availableColumns)
    .filter((column) => allowed.includes(column) && GL_DETAIL_NUMERIC_COLUMNS.has(column))
  const whereSql = buildCfoGlDetailWhereSql(state, availableColumns)
  const dateCols = allowed.includes('txn_date')
    ? [
        `CAST(MIN(TRY_CAST(${duckDbIdent('txn_date')} AS DATE)) AS VARCHAR) AS min_txn_date`,
        `CAST(MAX(TRY_CAST(${duckDbIdent('txn_date')} AS DATE)) AS VARCHAR) AS max_txn_date`,
      ]
    : [`'' AS min_txn_date`, `'' AS max_txn_date`]
  const totalCols = numericColumns.map((column) => `SUM(COALESCE(TRY_CAST(${duckDbIdent(column)} AS DOUBLE), 0)) AS ${duckDbIdent(`total_${column}`)}`)
  return [
    `SELECT COUNT(*) AS row_count,`,
    `  ${[...dateCols, ...totalCols].join(',\n  ')}`,
    `FROM ${duckDbIdent(tableName)}`,
    whereSql,
  ].filter(Boolean).join('\n')
}

export function buildCfoGlDetailCountSql(args: {
  state: CfoGlDetailQueryState
  availableColumns: string[]
  tableName?: string
}): string {
  const whereSql = buildCfoGlDetailWhereSql(args.state, args.availableColumns)
  return [`SELECT COUNT(*) AS row_count FROM ${duckDbIdent(args.tableName || QBO_GL_DETAIL_TABLE)}`, whereSql].filter(Boolean).join('\n')
}

export function buildCfoGlDetailDistinctValuesSql(args: {
  state: CfoGlDetailQueryState
  availableColumns: string[]
  column: string
  tableName?: string
  limit?: number
  filterIndex?: number
  search?: string
  sort?: CfoGlDetailDistinctValueSort
}): string {
  const column = requireAllowedGlColumn(args.column, args.availableColumns)
  const stateWithoutFilter: CfoGlDetailQueryState = {
    ...args.state,
    filter: args.state.filter?.column === column ? { column: '', values: [] } : args.state.filter,
    filters: effectiveGlFilters(args.state).filter((filter, index) => (
      index !== args.filterIndex && normalizeGlDetailColumnId(filter.column) !== column
    )),
  }
  const whereSql = buildCfoGlDetailWhereSql(stateWithoutFilter, args.availableColumns)
  const search = String(args.search || '').trim().toLowerCase()
  const searchPredicate = search
    ? `lower(CAST(${duckDbIdent(column)} AS VARCHAR)) LIKE ${sqlString(`%${search.replace(/[%_\\]/g, '\\$&')}%`)} ESCAPE '\\'`
    : ''
  const filteredWhereSql = searchPredicate
    ? whereSql
      ? `${whereSql} AND ${searchPredicate}`
      : `WHERE ${searchPredicate}`
    : whereSql
  const limit = Math.max(1, Math.min(500, Math.floor(Number(args.limit || 100))))
  const orderSql = args.sort === 'value' ? `ORDER BY value ASC` : `ORDER BY row_count DESC, value ASC`
  return [
    `SELECT CAST(${duckDbIdent(column)} AS VARCHAR) AS value, COUNT(*) AS row_count`,
    `FROM ${duckDbIdent(args.tableName || QBO_GL_DETAIL_TABLE)}`,
    filteredWhereSql,
    `GROUP BY 1`,
    orderSql,
    `LIMIT ${limit}`,
  ].filter(Boolean).join('\n')
}

export function parseCfoGlDetailTotals(row: Record<string, any>, numericColumns: string[]): CfoGlDetailTotals {
  const valueFor = (...keys: string[]): any => {
    for (const key of keys) {
      if (Object.prototype.hasOwnProperty.call(row, key)) return row[key]
    }
    const folded = new Map<string, any>()
    for (const [key, value] of Object.entries(row || {})) {
      folded.set(String(key).replace(/[_\s]/g, '').toLowerCase(), value)
    }
    for (const key of keys) {
      const value = folded.get(String(key).replace(/[_\s]/g, '').toLowerCase())
      if (value !== undefined) return value
    }
    return undefined
  }
  const totals: Record<string, number> = {}
  for (const column of numericColumns) {
    const value = Number(valueFor(`total_${column}`) ?? 0)
    totals[column] = Number.isFinite(value) ? value : 0
  }
  const rowCount = Number(valueFor('row_count', 'rowCount') ?? 0)
  return {
    rowCount: Number.isFinite(rowCount) ? Math.max(0, Math.floor(rowCount)) : 0,
    minDate: String(valueFor('min_txn_date', 'minTxnDate') || ''),
    maxDate: String(valueFor('max_txn_date', 'maxTxnDate') || ''),
    totals,
  }
}

export function formatGlNumber(value: number | null | undefined): string {
  const n = Number(value || 0)
  return n.toLocaleString(undefined, {
    maximumFractionDigits: Math.abs(n) >= 1000 ? 0 : 2,
    minimumFractionDigits: Math.abs(n) > 0 && Math.abs(n) < 1000 ? 2 : 0,
  })
}

export function formatGlCell(value: any, column: string): string {
  if (value == null || value === '') return ''
  if (GL_DETAIL_NUMERIC_COLUMNS.has(column)) return formatGlNumber(Number(value || 0))
  if (column === 'is_adjustment') return value ? 'Yes' : ''
  return String(value)
}

export function sortLabel(column: string, direction: CfoGlDetailSortDirection): string {
  return `${glDetailColumnLabel(column)} ${direction === 'asc' ? 'ascending' : 'descending'}`
}
